import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import '../css/SRAComponent.css';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';


const SRAComponent = () => {
  const [sras, setSras] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [srasPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    platform: '',
    instrument_model: '',
    collection_date: '',
    geo_loc_name: '',
    library_strategy: '',
  });

  const [modalSRA, setModalSRA] = useState(null);

  const platformOptions = ['', 'ILLUMINA', 'OXFORD_NANOPORE'];
  const instrumentOptions = [
    '',
    'Illumina HiSeq 2000',
    'Illumina HiSeq 2500',
    'Illumina HiSeq 4000',
    'Illumina MiSeq',
    'Illumina NovaSeq 6000',
    'NextSeq 500',
  ];
  const libraryOptions = ['', 'WGS', 'RNA-Seq', 'GBS', 'Amplicon'];
  const collectionDateOptions = ['', '2013-03', '2018-07-11', '2020', '2021-11-25'];
  const geoLocOptions = [
    '', 'Ethiopia', 'Ethiopia:Amhara', 'Ethiopia:Benishangul_Gumuz',
    'Ethiopia: EIAR Alem Tena field site', 'Ethiopia:NA', 'Ethiopia:Oromia',
    'Ethiopia:SNNP', 'Ethiopia:Tigray', 'Israel', 'Israel:Midreshet Ben Gurion',
    'missing', 'NA', 'not collected', 'Switzerland:Bern', 'Switzerland:Universit of Bern', 'USA'
  ];

  const downloadFiles = {
    csv: 'http://197.156.76.122/api/sra/download/csv/',
    xml: 'http://197.156.76.122/api/sra/download/xml/',
  };

  // 🔹 Build API URL with search & filters
  const buildUrl = useCallback(() => {
    let url = `http://197.156.76.122/api/sra/?page=${currentPage}&page_size=${srasPerPage}`;
    if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
    Object.entries(filters).forEach(([key, value]) => {
      if (value) url += `&${key}=${encodeURIComponent(value)}`;
    });
    return url;
  }, [currentPage, srasPerPage, searchQuery, filters]);

  // 🔹 Fetch SRA data
  const fetchSRAs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(buildUrl());
      if (!res.data?.results) throw new Error('No results returned');
      setSras(res.data.results);
      setTotalCount(res.data.count || 0);
    } catch (err) {
      console.error('❌ Failed to fetch SRA records:', err);
      setError('Failed to load SRA data.');
      setSras([]);
    } finally {
      setLoading(false);
    }
  }, [buildUrl]);

  useEffect(() => {
    fetchSRAs();
  }, [fetchSRAs]);

  const totalPages = Math.ceil(totalCount / srasPerPage);
  const firstItemNum = (currentPage - 1) * srasPerPage + 1;
  const lastItemNum = Math.min(currentPage * srasPerPage, totalCount);

  // 🔹 Pagination
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // 🔹 Filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  // 🔹 Open/close modal
  const openModal = (sra) => {
    setModalSRA(sra);
    window.history.pushState({ modal: true }, 'SRA Details', `#${sra.experiment_accession}`);
  };
  const closeModal = () => {
    setModalSRA(null);
    window.history.back();
  };

  // 🔹 Copy modal content
  const copyModalContent = () => {
    if (!modalSRA) return;
    const content = `
Experiment: ${modalSRA.experiment_title}
Accession: ${modalSRA.experiment_accession}
Run: ${modalSRA.run_accession || 'N/A'}
Study Abstract: ${modalSRA.study_abstract || 'N/A'}
Platform: ${modalSRA.platform || 'N/A'}
Instrument: ${modalSRA.instrument_model || 'N/A'}
Collection Date: ${modalSRA.collection_date || 'N/A'}
Geo Location: ${modalSRA.geo_loc_name || 'N/A'}
Library Strategy: ${modalSRA.library_strategy || 'N/A'}
Study Title: ${modalSRA.study_title || 'N/A'}
`;
    navigator.clipboard.writeText(content).then(() => alert('✅ Modal content copied!'));
  };

  useEffect(() => {
    const handlePopState = () => setModalSRA(null);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <div className="sra-container">
        {/* 🔙 Back to Home (ADD THIS HERE) */}
	    <button 
	      className="back-button"
	      onClick={() => navigate('/')}
	    >
	      <FaArrowLeft /> Back to Home
	    </button>
      {/* HEADER */}
      <div className="sra-header">
        <h2>SRA Search Results</h2>
      </div>

      <div className="sra-layout">
        {/* FILTERS */}
        <div className="filter-panel">
          <h4>Filter</h4>
          {[
            { label: 'Platform', name: 'platform', options: platformOptions },
            { label: 'Instrument', name: 'instrument_model', options: instrumentOptions },
            { label: 'Library Strategy', name: 'library_strategy', options: libraryOptions },
            { label: 'Collection Date', name: 'collection_date', options: collectionDateOptions },
            { label: 'Geo Location', name: 'geo_loc_name', options: geoLocOptions },
          ].map(f => (
            <div className="filter-group" key={f.name}>
              <label>{f.label}:</label>
              <select name={f.name} value={filters[f.name]} onChange={handleFilterChange}>
                {f.options.map(opt => (
                  <option key={opt} value={opt}>{opt || 'All'}</option>
                ))}
              </select>
            </div>
          ))}
        </div>

        {/* SRA LIST */}
        <div className="sra-content">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search SRAs..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
          </div>

          {loading && <p>⏳ Loading...</p>}
          {error && <p className="error">{error}</p>}
          {!loading && !error && sras.length === 0 && <p>No SRA records found.</p>}

          {!loading && !error && sras.length > 0 && (
            <>
              <p className="results-summary">
                Items: {firstItemNum} to {lastItemNum} of {totalCount}
              </p>

              <div className="sra-list">
                {sras.map((sra, idx) => (
                  <div key={sra.experiment_accession} className="sra-item">
                    <div className="item-index">{idx + 1 + (currentPage - 1) * srasPerPage}.</div>

                    <span className="sra-title-link" onClick={() => openModal(sra)}>
                      {sra.experiment_title || 'No title'}
                    </span>

                    <div className="sra-accession">
                      Accession:{" "}
                      <span className="clickable" onClick={() => openModal(sra)}>
                        {sra.experiment_accession}
                      </span>
                    </div>

                    {sra.run_accession && (
                      <div className="sra-accession">
                        Run:{" "}
                        <span className="clickable" onClick={() => openModal(sra)}>
                          {sra.run_accession}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="pagination-controls bottom">
                <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>&lt;&lt; First</button>
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>&lt; Prev</button>
                <span>{currentPage} of {totalPages}</span>
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next &gt;</button>
                <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>Last &gt;&gt;</button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* FULL PAGE MODAL */}
      {modalSRA && (
        <div className="modal-overlay fullpage" onClick={closeModal}>
          <div className="modal-content fullpage" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            <div className="modal-header">
              <h2>{modalSRA.experiment_title}</h2>
              <button className="back-button" onClick={closeModal}>← Back</button>
              <button className="copy-button" onClick={copyModalContent}>📋 Copy</button>
            </div>
            <div className="modal-links">
              <h3>Related NCBI Links</h3>
              <p>
                <a href={`https://www.ncbi.nlm.nih.gov/sra/?term=${modalSRA.experiment_accession}`} target="_blank" rel="noreferrer">
                  📌 SRA Record
                </a>
              </p>
              {modalSRA.bioproject_accession && (
                <p>
                  <a href={`https://www.ncbi.nlm.nih.gov/bioproject/${modalSRA.bioproject_accession}`} target="_blank" rel="noreferrer">
                    📁 BioProject: {modalSRA.bioproject_accession}
                  </a>
                </p>
              )}
              {modalSRA.biosample_accession && (
                <p>
                  <a href={`https://www.ncbi.nlm.nih.gov/biosample/${modalSRA.biosample_accession}`} target="_blank" rel="noreferrer">
                    🧪 BioSample: {modalSRA.biosample_accession}
                  </a>
                </p>
              )}
              {modalSRA.run_accession && (
                <p>
                  <a href={`https://trace.ncbi.nlm.nih.gov/Traces/sra/?run=${modalSRA.run_accession}`} target="_blank" rel="noreferrer">
                    ▶ Run Browser
                  </a>
                </p>
              )}
            </div>
            <div className="modal-body">
              {modalSRA.study_title && <p><strong>Study Title:</strong> {modalSRA.study_title}</p>}
              {modalSRA.study_abstract && <p><strong>Abstract:</strong> {modalSRA.study_abstract}</p>}
              {modalSRA.platform && <p><strong>Platform:</strong> {modalSRA.platform}</p>}
              {modalSRA.instrument_model && <p><strong>Instrument:</strong> {modalSRA.instrument_model}</p>}
              {modalSRA.collection_date && <p><strong>Collection Date:</strong> {modalSRA.collection_date}</p>}
              {modalSRA.geo_loc_name && <p><strong>Geo Location:</strong> {modalSRA.geo_loc_name}</p>}
              {modalSRA.library_strategy && <p><strong>Library Strategy:</strong> {modalSRA.library_strategy}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SRAComponent;

